/**
 * Hook para testar uma consulta do node "Conexão" direto no modal de configuração
 * -----------------------------------------------------------------------------
 * Executa a operação real do provider pela edge function `integration-gateway`
 * (ação `execute`) e devolve o payload retornado + caminhos sugeridos para o
 * mapeamento genérico de variáveis (mesmo modelo do node Webhook).
 *
 * Provider-agnóstico: nenhuma regra específica de fornecedor.
 *
 * CHANGELOG:
 *   RELEASE: 2026-08-29 v1.1.0 - Consultas assíncronas: expõe `pending` e a
 *            mensagem do provider (o retorno ainda não é o final).
 *   RELEASE: 2026-08-29 v1.0.0 - Versão inicial.
 */

import { useCallback, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';
import { extractJsonPaths } from '@/lib/flow/jsonPaths';

const log = createLogger('useConnectionTest');

export interface ConnectionTestParams {
  connectionId: string;
  operation: string;
  params: Record<string, unknown>;
}

export interface ConnectionTestResult {
  success: boolean;
  /** Payload sanitizado retornado pelo provider (sem base64 de PDF) */
  data?: unknown;
  /** ID da consulta gravada no histórico (permite baixar o PDF) */
  consultationId?: string;
  hasPdf?: boolean;
  pdfFilename?: string | null;
  durationMs?: number;
  error?: string;
  /** Provider aceitou a consulta mas ainda está processando (retorno não final) */
  pending?: boolean;
  pendingMessage?: string | null;
}

export function useConnectionTest() {
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<ConnectionTestResult | null>(null);
  const [suggestedPaths, setSuggestedPaths] = useState<string[]>([]);

  const clearResult = useCallback(() => {
    setResult(null);
    setSuggestedPaths([]);
  }, []);

  const runTest = useCallback(
    async (input: ConnectionTestParams): Promise<ConnectionTestResult> => {
      setIsLoading(true);
      setResult(null);
      setSuggestedPaths([]);

      const startedAt = Date.now();

      try {
        const { data, error } = await supabase.functions.invoke('integration-gateway', {
          body: {
            action: 'execute',
            connection_id: input.connectionId,
            operation: input.operation,
            params: input.params,
          },
        });

        if (error) throw new Error(error.message);

        const payload = (data ?? {}) as {
          success?: boolean;
          data?: unknown;
          consultation_id?: string;
          pdf?: { has_pdf?: boolean; filename?: string | null };
          pending?: boolean;
          pending_message?: string | null;
          error?: string;
        };

        if (payload.success === false) {
          throw new Error(payload.error || 'Falha ao executar a consulta');
        }

        const testResult: ConnectionTestResult = {
          success: true,
          data: payload.data,
          consultationId: payload.consultation_id,
          hasPdf: Boolean(payload.pdf?.has_pdf),
          pdfFilename: payload.pdf?.filename ?? null,
          durationMs: Date.now() - startedAt,
          pending: Boolean(payload.pending),
          pendingMessage: payload.pending_message ?? null,
        };

        setResult(testResult);
        const paths = extractJsonPaths(payload.data);
        setSuggestedPaths(paths);
        log.debug('Teste de conexão concluído', { paths: paths.length });

        return testResult;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Erro inesperado no teste';
        log.error('Falha no teste de conexão', { message });
        const failed: ConnectionTestResult = {
          success: false,
          error: message,
          durationMs: Date.now() - startedAt,
        };
        setResult(failed);
        return failed;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  return { isLoading, result, suggestedPaths, runTest, clearResult };
}
